import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "../../api/client";
import type { Deployment, Exercise, WeatherCustom, Zone } from "../../api/types";
import { runInDossierScope } from "../../map/dossierWrites";
import { orderActions, ordersNote, refreshOrders } from "../../map/orderSync";
import { openRequested, useTerrainWeatherRequest, ZONE_TARGETS } from "../../map/zones/useZones";
import { showZoneWait } from "../../map/zones/zoneAttention";
import { zoneBusy } from "../../map/zones/zoneState";
import { t, tServer } from "../../i18n/i18n";
import { DisclosureRow } from "../../components/panel";
import { Badge } from "../../components/ui";
import { useOpenDossier } from "../../sharing/access";
import { type DeploymentParams, useUi } from "../../state/ui";
import { choiceLabel, customOf, previewLine, SEASONAL, weatherGap, weatherKey, weatherState } from "../../weather";
import { pictogramIcon } from "../../weatherIcons";
import { EMPTY_PARAMETERS, rebaseStoredDraft } from "../missionDraft";
import { provisionalHint, useWeatherOptions, type WeatherPending, WeatherField } from "../WeatherField";

/** Vues qui lisent la météo appliquée du dossier, relues après son changement (réglage de l'étape Terrain,
 *  enregistrement des Données initiales) : réglage Météo (toutes cartes), logistique, DIREX et sa situation,
 *  cohérence, modes d'action ennemis. Les ordres suivent `refreshOrders`. */
const WEATHER_VIEWS = ["logistics", "direx", "situation", "coherence", "enemy-coa"] as const;

export function refreshWeatherViews(queryClient: QueryClient, exerciseId: string): void {
  void queryClient.invalidateQueries({ queryKey: ["weather-options"] });
  for (const key of WEATHER_VIEWS) void queryClient.invalidateQueries({ queryKey: [key, exerciseId] });
}

/** Attente après le dernier choix fait aux flèches du clavier avant d'écrire le dossier : parcourir les tuiles
 *  n'écrit (ni ne recompose les ordres) qu'une fois, pour la tuile où l'on s'arrête. */
export const KEYBOARD_WRITE_DELAY_MS = 600;

/** Météo du dossier : scénario et choix, posés ensemble. */
interface Weather {
  scenario: string | null;
  custom: WeatherCustom | null;
}

/** Écriture du réglage : la météo écrite, celle d'avant (« Annuler » de l'avis), et si c'est déjà ce retour. */
interface WeatherWrite {
  next: Weather;
  before: Weather;
  undo: boolean;
}

/** Corps de l'écriture : les paramètres du dossier, météo remplacée. */
export function weatherPatch(ex: Pick<Exercise, "parameters">, next: Weather): { parameters: Exercise["parameters"] } {
  return {
    parameters: { ...EMPTY_PARAMETERS, ...ex.parameters, weather_scenario: next.scenario ?? SEASONAL, weather_custom: next.custom },
  };
}

/** Réglage « Météo de l'exercice » de l'étape Terrain de la vue Manœuvre (chantier de cohérence météo, décision du
 *  30/10/2026) : le même réglage que les Données initiales (`WeatherField`), sous le climat du terrain libre ou sous
 *  la ligne du camp — la météo se règle juste après avoir placé le terrain, sans revenir aux Données initiales. Un
 *  champ du panneau, ni un rendu ni un outil de la carte. Il lit le dossier enregistré (carte, date, fuseau, météo)
 *  et l'écrit directement, version contrôlée, dans la portée des écritures du dossier ; les avis du serveur (choix
 *  ramené au terrain) rejoignent l'avis « Météo de l'exercice enregistrée », qui offre « Annuler ». Aucune écriture
 *  ne part seule : ni réajustement ni écart corrigé sans geste. Brouillon des Données initiales modifié, ou lecture
 *  seule : l'appui désigne « Enregistrer » ou les droits (`onRefused`). Terrain libre pas encore prêt : « Choisie »
 *  se règle sur son climat estimé (confirmé à la fin de la préparation) ; un serveur qui ne l'estime pas la laisse
 *  estompée, l'appui désigne sa préparation. Jamais en CENTAC. `mapSlug` : carte affichée (celle d'un brouillon
 *  n'est pas celle du dossier : rien n'est montré). */
export function TerrainWeather({
  zone,
  mapSlug,
  canEdit,
  onRefused,
  className,
}: {
  zone: Zone | null;
  mapSlug: string | null;
  canEdit: boolean;
  onRefused: () => void;
  /** Marges seulement. */
  className?: string;
}) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const exercise = useOpenDossier();
  // Météo montrée pendant les écritures en vol : le geste suivant part d'elle (écritures en file dans la portée du
  // dossier, chacune sur la version laissée par la précédente) ; oubliée quand tout est répondu.
  const [shown, setShown] = useState<Weather | null>(null);
  const inFlight = useRef(0);
  const save = useMutation({
    mutationFn: ({ next }: WeatherWrite) => {
      const id = exercise!.id;
      return runInDossierScope(queryClient, id, async () => {
        // Version la plus récente du dossier (écritures de la carte déjà parties comprises), lue au départ.
        const latest = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === id) ?? exercise!;
        const ex = await api.updateExercise(latest, weatherPatch(latest, next));
        // Dans la portée : l'écriture suivante (de la carte ou de ce réglage) part de la nouvelle version.
        queryClient.setQueryData<Exercise[]>(["exercises"], (list) => list?.map((e) => (e.id === ex.id ? { ...e, ...withoutRefresh(ex) } : e)));
        queryClient.setQueriesData<Deployment>(
          { predicate: (q) => q.queryKey[0] === "deployment" && (q.queryKey[1] as DeploymentParams | null)?.exercise_id === ex.id },
          (d) => (d && d.exercise_version != null ? { ...d, exercise_version: ex.version } : d),
        );
        return ex;
      });
    },
    onSuccess: (ex, written) => {
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      if (ex.refreshed) refreshOrders(queryClient, ex.id);
      refreshWeatherViews(queryClient, ex.id);
      // Brouillon des Données initiales gardé sur ce poste : la météo y prend la valeur écrite, le reste est gardé.
      rebaseStoredDraft(ex, ["parameters.weather_scenario", "parameters.weather_custom"]);
      const undo = written.undo
        ? undefined
        : { label: t("Annuler"), run: () => write(written.before, true) };
      const refreshed = ex.refreshed ?? null;
      notify({
        text: written.undo ? t("Météo de l'exercice rétablie") : t("Météo de l'exercice enregistrée"),
        detail: refreshed ? ordersNote(refreshed, { source: "dossier" }) || undefined : undefined,
        tone: "ok",
        ...(undo || refreshed ? orderActions(undo, refreshed ?? {}) : {}),
      });
    },
    onError: (error) => {
      setShown(null);
      // Dossier modifié entre-temps (autre poste, autre écriture) : relu, le réglage montre son état ; rien n'est
      // réécrit d'office.
      if (error instanceof ApiError && (error.status === 412 || error.status === 409)) {
        void queryClient.invalidateQueries({ queryKey: ["exercises"] });
        notify({
          text: t("Météo non enregistrée"),
          detail: t("Le dossier a changé entre-temps : il vient d'être relu, choisissez de nouveau la météo."),
          tone: "warn",
        });
        return;
      }
      notify({
        text: t("Météo non enregistrée"),
        detail: error instanceof ApiError ? tServer(error.message) : t("Serveur injoignable"),
        tone: "error",
      });
    },
    onSettled: () => {
      inFlight.current -= 1;
      if (inFlight.current <= 0) {
        inFlight.current = 0;
        setShown(null);
      }
    },
  });
  const current: Weather = shown ?? { scenario: exercise?.parameters.weather_scenario ?? null, custom: exercise?.parameters.weather_custom ?? null };
  // Choix fait aux flèches, montré tout de suite (`shown`) et écrit après `KEYBOARD_WRITE_DELAY_MS` sans autre
  // flèche ; la météo d'avant est celle du premier choix de la série (« Annuler » y revient).
  const deferred = useRef<{ next: Weather; before: Weather; timer: ReturnType<typeof setTimeout> } | null>(null);
  const flush = () => {
    const waiting = deferred.current;
    if (!waiting) return;
    clearTimeout(waiting.timer);
    deferred.current = null;
    save.mutate({ next: waiting.next, before: waiting.before, undo: false });
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  // Réglage quitté pendant l'attente : le dernier choix est écrit.
  useEffect(() => () => flushRef.current(), []);
  const write = (next: Weather, undo = false) => {
    if (!exercise) return;
    const waiting = deferred.current;
    if (waiting) {
      // Clic après des flèches : une seule écriture, depuis la météo d'avant la série.
      clearTimeout(waiting.timer);
      deferred.current = null;
    } else {
      inFlight.current += 1;
    }
    setShown(next);
    save.mutate({ next, before: waiting?.before ?? current, undo });
  };
  const defer = (next: Weather) => {
    if (!exercise) return;
    const waiting = deferred.current;
    if (waiting) clearTimeout(waiting.timer);
    else inFlight.current += 1;
    setShown(next);
    deferred.current = { next, before: waiting?.before ?? current, timer: setTimeout(() => flushRef.current(), KEYBOARD_WRITE_DELAY_MS) };
  };
  const shownHere = Boolean(
    exercise && exercise.parameters.level !== "CENTAC" && exercise.map_slug && exercise.map_slug === mapSlug && (!zone || zone.slug === exercise.map_slug),
  );
  const p = exercise?.parameters;
  // Terrain libre pas encore prêt : climat provisoire (l'aperçu de la saison reste lu, la météo choisie attend).
  const preparing = Boolean(zone && zone.status !== "ready");
  const { options, query } = useWeatherOptions({
    mapSlug: shownHere ? (exercise?.map_slug ?? null) : null,
    startDtg: p?.start_dtg ?? null,
    timeZone: p?.time_zone ?? null,
    scenario: current.scenario,
    custom: current.custom,
  });
  const gap = options && !query.isPlaceholderData && !preparing ? weatherGap(options, weatherKey(p ? { ...p, ...params(current) } : null)) : null;
  // Replié par défaut ; déplié quand la météo enregistrée n'est pas appliquée telle quelle, ou à la demande (avis
  // « Terrain prêt », « Régler la météo »).
  const hasGap = Boolean(gap);
  const [open, setOpen] = useState(() => openRequested() || hasGap);
  const requested = useTerrainWeatherRequest((s) => s.at);
  useEffect(() => {
    if (requested && openRequested()) setOpen(true);
  }, [requested]);
  useEffect(() => {
    if (hasGap) setOpen(true);
  }, [hasGap]);
  if (!shownHere || !exercise || !p) return null;
  const pending: WeatherPending | null =
    zone && preparing
      ? {
          provisional: true,
          hint: zoneBusy(zone) ? provisionalHint() : t("Préparation du terrain interrompue : la météo choisie se règle une fois le terrain prêt."),
          onBlocked: () => {
            // Préparation échouée ou annulée : rien ne finira sans « Réessayer », que `showZoneWait` désigne.
            notify({
              text: zoneBusy(zone)
                ? t("Terrain pas encore prêt : la météo choisie se règle dès que sa préparation est finie.")
                : t("Préparation du terrain interrompue : relancez-la pour régler la météo choisie."),
              tone: "info",
            });
            showZoneWait(zone);
          },
        }
      : null;
  // Mode de la météo APPLIQUÉE (celle de l'aperçu) : un choix impossible ici s'applique comme la saisonnière.
  const state = gap?.kind === "impossible" ? "seasonal" : weatherState(params(current));
  const mode = state === "custom" ? t("Choisie") : state === "imported" ? t("Météo de l'ordre importé") : t("Saisonnière");
  // Terrain en préparation sur un serveur qui estime son climat : « Choisie » est déjà offerte ; le résumé dit que ce
  // climat est estimé.
  const chosen = customOf(params(current));
  const estimated = preparing && Boolean(options?.approximate);
  const summary = estimated
    ? t("{mode} · {meteo} — climat estimé, confirmé à la fin de la préparation", {
        mode,
        meteo: chosen ? choiceLabel(chosen, options?.regional_wind) : options ? previewLine(options) : "",
      })
    : preparing
    ? zone && !zoneBusy(zone)
      ? t("Préparation interrompue : la météo choisie attend le terrain prêt.")
      : t("Réglable dès que le terrain est prêt : la météo proposée dépend de son climat.")
    : options?.preview
      ? `${mode} · ${previewLine(options)}`
      : mode;
  return (
    <div data-attention={ZONE_TARGETS.weather} className={clsx("rounded-lg", className)}>
      <DisclosureRow
        icon={pictogramIcon(options?.preview?.pictogram)}
        title={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>{t("Météo de l'exercice")}</span>
            {gap && (
              <Badge dot tone="warn">
                {t("À revoir")}
              </Badge>
            )}
            <span className="block w-full text-meta leading-snug font-normal text-ink-muted">{summary}</span>
          </span>
        }
        open={open}
        onToggle={() => setOpen((o) => !o)}
      >
        <div className="space-y-1.5 px-2 pt-1 pb-2">
          <WeatherField
            attention="zone-weather-field"
            label={t("Météo")}
            mapSlug={exercise.map_slug}
            startDtg={p.start_dtg}
            timeZone={p.time_zone}
            scenario={current.scenario}
            custom={current.custom}
            onChange={(scenario, custom, how) => (how?.deferred ? defer({ scenario, custom }) : write({ scenario, custom }))}
            pending={pending}
            locked={canEdit ? undefined : onRefused}
          />
          {zone && zone.exercises > 1 && (
            <p className="text-meta leading-snug text-ink-muted">
              {t("Propre à ce dossier ; le climat, au-dessus, vaut pour les {n} dossiers de ce terrain.", { n: zone.exercises })}
            </p>
          )}
        </div>
      </DisclosureRow>
    </div>
  );
}

/** Paramètres de météo d'un état du réglage (`weatherState`, `weatherKey`). */
function params(w: Weather): { weather_scenario: string | null; weather_custom: WeatherCustom | null } {
  return { weather_scenario: w.scenario, weather_custom: w.custom };
}

/** Dossier tel que la liste le range : sans le relevé des ordres recomposés (`refreshed`), propre à la réponse. */
function withoutRefresh(ex: Exercise): Exercise {
  const { refreshed: _refreshed, ...rest } = ex;
  return rest;
}
